import { useState, useEffect, useCallback } from "react";
import apiClient from "../../api/client";
import { getUser } from "../../api/auth";
import "./SectionDiscussion.css";

// ── Time formatter ────────────────────────────────────────────────────────────
const timeAgo = (dateStr) => {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// ── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = ({ name, isAdmin }) => {
  const initials =
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?";
  return (
    <div className={`disc-avatar ${isAdmin ? "disc-avatar--admin" : ""}`}>
      {initials}
    </div>
  );
};

// ── Single comment ────────────────────────────────────────────────────────────
const Comment = ({
  comment,
  currentUser,
  onReply,
  onDelete,
  isReply = false,
}) => {
  const isOwn = currentUser?.id === comment.user.id;
  const isAdmin = currentUser?.isAdmin;
  const canDelete = isOwn || isAdmin;

  return (
    <div className={`disc-comment ${isReply ? "disc-comment--reply" : ""}`}>
      <Avatar name={comment.user.name} isAdmin={comment.user.isAdmin} />
      <div className="disc-comment__body">
        <div className="disc-comment__meta">
          <span className="disc-comment__name">
            {comment.user.name}
            {comment.user.isAdmin && <span className="disc-badge">Author</span>}
          </span>
          <span className="disc-comment__time">
            {timeAgo(comment.createdAt)}
          </span>
        </div>
        <div
          className={`disc-comment__text ${comment.isDeleted ? "disc-comment__text--deleted" : ""}`}
        >
          {comment.content}
        </div>
        {!comment.isDeleted && (
          <div className="disc-comment__actions">
            {!isReply && (
              <button
                className="disc-action-btn"
                onClick={() => onReply(comment.id)}
              >
                Reply
              </button>
            )}
            {canDelete && (
              <button
                className="disc-action-btn disc-action-btn--danger"
                onClick={() => onDelete(comment.id)}
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Comment input ─────────────────────────────────────────────────────────────
const CommentInput = ({ placeholder, onSubmit, onCancel, loading }) => {
  const [text, setText] = useState("");

  // Clears the box only once the post has landed. It used to clear
  // unconditionally, synchronously, before the request resolved, so a failed
  // post (an expired session being the likely one) silently ate what the user
  // had written.
  const handleSubmit = async () => {
    if (!text.trim()) return;
    const posted = await onSubmit(text.trim());
    if (posted) setText("");
  };

  return (
    <div className="disc-input-wrap">
      <textarea
        className="disc-textarea"
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        maxLength={2000}
      />
      <p className="disc-helper-text">Markdown supported · Be kind</p>
      <div className="disc-input-actions">
        {onCancel && (
          <button className="disc-btn disc-btn--ghost" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          className="disc-btn disc-btn--primary"
          onClick={handleSubmit}
          disabled={loading || !text.trim()}
        >
          {loading ? "Posting..." : "Post"}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Main SectionDiscussion component
// ═══════════════════════════════════════════════════════════════════════════════
const SectionDiscussion = ({ workbookId, sectionId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // comment id
  const [error, setError] = useState("");

  const currentUser = getUser();
  const adminEmail =
    process.env.REACT_APP_ADMIN_EMAIL || "founder@scriptedcad.com";
  const enrichedUser = currentUser
    ? { ...currentUser, isAdmin: currentUser.email === adminEmail }
    : null;

  // Load comments
  const loadComments = useCallback(async () => {
    try {
      const data = await apiClient.get(
        `/api/comments/${workbookId}/${sectionId}`,
      );
      setComments(data ?? []);
    } catch (err) {
      // /api/comments is [Authorize]. A 401 here used to be swallowed after the
      // API client had already navigated the reader off the page; now it stays
      // put and says what happened.
      if (err?.status === 401) setError("Your session expired. Sign in again to join the discussion.");
      else console.error("Failed to load comments", err);
    } finally {
      setLoading(false);
    }
  }, [workbookId, sectionId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  // Post comment or reply
  // Returns true when the comment was accepted, so CommentInput knows whether
  // it is safe to clear the box.
  const handlePost = async (content, parentId = null) => {
    setPosting(true);
    setError("");
    try {
      const body = { workbookId, sectionId, content };
      if (parentId != null) body.parentId = parentId;
      const newComment = await apiClient.post("/api/comments", body);
      console.log("Posted comment response:", newComment); // ← add this

      if (parentId) {
        // Add reply to parent
        setComments((prev) =>
          prev.map((c) =>
            c.id === parentId
              ? { ...c, replies: [...(c.replies || []), newComment] }
              : c,
          ),
        );
        setReplyingTo(null);
      } else {
        setComments((prev) => [...prev, { ...newComment, replies: [] }]);
      }
      return true;
    } catch (err) {
      setError(
        err?.status === 401
          ? "Your session expired. Sign in again to join the discussion."
          : "Failed to post. Please try again.",
      );
      return false;
    } finally {
      setPosting(false);
    }
  };

  // Delete comment
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await apiClient.delete(`/api/comments/${id}`);
      // Soft delete in UI
      setComments((prev) =>
        prev.map((c) => {
          if (c.id === id)
            return { ...c, isDeleted: true, content: "[deleted]" };
          return {
            ...c,
            replies: (c.replies || []).map((r) =>
              r.id === id ? { ...r, isDeleted: true, content: "[deleted]" } : r,
            ),
          };
        }),
      );
    } catch (err) {
      setError(
        err?.status === 401
          ? "Your session expired. Sign in again to join the discussion."
          : "Failed to delete. Please try again.",
      );
    }
  };

  const totalCount = comments.reduce(
    (acc, c) => acc + 1 + (c.replies?.length || 0),
    0,
  );

  return (
    <div className="disc-root">
      <div className="disc-header">
        <span className="disc-title">Discussion</span>
        {totalCount > 0 && <span className="disc-count">{totalCount}</span>}
      </div>

      {/* New comment input */}
      <CommentInput
        placeholder="Ask a question or share a thought..."
        onSubmit={(text) => handlePost(text)}
        loading={posting && !replyingTo}
      />

      {error && <div className="disc-error">{error}</div>}

      {/* Comments list */}
      {loading ? (
        <div className="disc-loading">Loading discussion...</div>
      ) : comments.length === 0 ? (
        <div className="disc-empty">
          No comments yet — be the first to ask a question.
        </div>
      ) : (
        <div className="disc-list">
          {comments.map((comment) => (
            <div key={comment.id} className="disc-thread">
              <Comment
                comment={comment}
                currentUser={enrichedUser}
                onReply={(id) => setReplyingTo(replyingTo === id ? null : id)}
                onDelete={handleDelete}
              />

              {/* Replies */}
              {(comment.replies || []).map((reply) => (
                <Comment
                  key={reply.id}
                  comment={reply}
                  currentUser={enrichedUser}
                  onReply={() => {}}
                  onDelete={handleDelete}
                  isReply
                />
              ))}

              {/* Reply input */}
              {replyingTo === comment.id && (
                <div className="disc-reply-input">
                  <CommentInput
                    placeholder={`Reply to ${comment.user.name}...`}
                    onSubmit={(text) => handlePost(text, comment.id)}
                    onCancel={() => setReplyingTo(null)}
                    loading={posting}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SectionDiscussion;
